import { Box } from '@mui/material'
import type React from 'react'
import styled from 'styled-components'

import DocumentRequests from '@/components/DocumentRequests'
import GuideMe from '@/components/GuideMe'
import { Body, ButtonsContainer } from '@/components/IdentityCard/IdentityCard'
import ReopenSection from '@/components/ReopenSection/ReopenSection'
import { SectionName } from '@/components/ReopenSection/types'
import VerifyCredit from '@/components/VerifyCredit'
import { OutcomeSectionName } from '@/lib/apollo/model/outcome'
import type { Applicant } from '@/lib/apollo/query/get-applicant'
import type { Application } from '@/types/Activity'
import { isFinalOutcome } from '@/utils/outcome'
import { isRefCompleted } from '@/utils/subjectHelpers'

import AdverseCredit from './AdverseCredit'
import ApplicantDeclarations from './ApplicantDeclarations'
import Confirmations from './Confirmations'
import { getGuideMeCreditData } from './utils'

const CreditCheckHeading = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  h2 {
    margin: 0;
  }

  @media (max-width: 2000px) {
    h2 {
      font-size: 20px;
    }
    img {
      width: 90px;
    }
  }
`

interface Props {
  application: Application
  applicant: Applicant
  isRestrictedReference: boolean
}

const CreditBody: React.FC<Props> = ({
  application,
  applicant,
  isRestrictedReference,
}) => {
  const creditProvider = application?.context?.credit?.provider || 'Equifax'

  const hasUnsatisfiedAdverseCredit =
    application?.context?.credit?.adverseCredit.some(
      item => item.status === 'Unsatisfied',
    ) ?? false

  const hasDeclaredCredit = (application.context?.credit?.declaredCredit?.length ?? 0) > 0

  const hasConfirmedIdentity =
    (application.context?.credit?.identityConfirmed ?? false) ||
    (application.context?.credit?.residencyConfirmed ?? false)

  const outcomeSections = application?.outcome?.sections ?? []
  const creditOutcome = outcomeSections.find(
    outcomeSection => outcomeSection.name === OutcomeSectionName.credit,
  )
  const creditOutcomeStatus = creditOutcome?.status

  const guideMeData = getGuideMeCreditData(creditOutcomeStatus)

  return (
    <Body>
      <ButtonsContainer>
        <GuideMe steps={guideMeData} />

        <DocumentRequests
          customerId={application.customerId}
          applicationId={application.applicationId}
          subjectId={application.subjectId}
          sectionName={SectionName.credit}
          subjectName={`${application.context?.firstName} ${application.context?.lastName}`}
          isRefCompleted={isRefCompleted(application)}
          disabled={isRestrictedReference}
        />
        <VerifyCredit
          customerId={applicant.customerId}
          subjectId={applicant.id}
          identityConfirmed={applicant.identityConfirmed}
          residencyConfirmed={applicant.residencyConfirmed}
          adverseCredit={applicant.adverseCredit ?? []}
          disabled={isFinalOutcome(creditOutcomeStatus) || isRestrictedReference}
        />
        {application.applicationId &&
          application.subjectId &&
          isFinalOutcome(creditOutcomeStatus) && (
            <ReopenSection
              customerId={application.customerId}
              applicationId={application.applicationId}
              subjectId={application.subjectId}
              sectionName={SectionName.credit}
              disabled={isRestrictedReference}
            />
          )}
      </ButtonsContainer>
      <Box sx={{ mt: 2.5 }}>
        <CreditCheckHeading>
          <h2>Credit Check</h2>
          <Box
            component="img"
            src={
              application.context?.credit?.provider === 'Equifax'
                ? 'https://vouchstatic.z33.web.core.windows.net/images/email/equifax_logo.png'
                : 'https://vouchstatic.z33.web.core.windows.net/images/email/experian_logo.png'
            }
            alt={`${application.context?.credit?.provider || 'Credit'} logo`}
            sx={{
              width: { xs: 90, sm: 120 },
              height: 40,
              objectFit: 'contain',
            }}
          />
        </CreditCheckHeading>
        <Confirmations
          identityConfirmed={application.context?.credit?.identityConfirmed}
          residencyConfirmed={application.context?.credit?.residencyConfirmed}
        />
        <AdverseCredit
          adverseCredit={application.context?.credit?.adverseCredit || []}
          hasConfirmedIdentity={hasConfirmedIdentity}
          hasDeclaredCredit={hasDeclaredCredit}
          provider={creditProvider}
        />
        <ApplicantDeclarations
          declaredCredit={application?.context?.credit?.declaredCredit || []}
          hasUnsatisfiedCredit={hasUnsatisfiedAdverseCredit}
        />
      </Box>
    </Body>
  )
}

export default CreditBody
